import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { LogOut, Download, RefreshCw, Search } from "lucide-react";
import SEO from "../components/SEO";
import FunnelStrip from "./FunnelStrip";
import EmailDeliveryPanel from "./EmailDeliveryPanel";

const STATUSES = ["Education/Nurture", "Growth Track", "Diagnostic", "Backend Buildout", "Brandon Review", "Not a Fit", "Onboarding"];

function triageButtonLabel(busy, triage) {
  if (busy) return "Analyzing…";
  return triage ? "Re-run AI Triage" : "AI Triage";
}

function buildContactsQuery(status, q) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (q) params.set("q", q);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [clarityApps, setClarityApps] = useState([]);
  const [triageBusy, setTriageBusy] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [session, setSession] = useState({ next_datetime: "", timezone_label: "CT", seats_remaining: 12, custom_note: "", is_active: true });
  const [sessionSaving, setSessionSaving] = useState(false);
  const [sessionSaved, setSessionSaved] = useState(false);
  const [paymentLinked, setPaymentLinked] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const meRes = await api.get("/auth/me");
      setMe(meRes.data);
      const [s, c, a, ca, cfg] = await Promise.all([
        api.get("/admin/analytics/summary"),
        api.get(`/admin/contacts${buildContactsQuery(statusFilter, search)}`),
        api.get("/admin/diagnostic-applications"),
        api.get("/admin/strategy-call-applications"),
        api.get("/config"),
      ]);
      setPaymentLinked(Boolean(cfg.data.wave_diagnostic_payment_url));
      setSummary(s.data);
      setContacts(c.data.items);
      setApplications(a.data.items);
      setClarityApps(ca.data.items);
    } catch {
      nav("/admin/login", { replace: true });
    } finally { setLoading(false); }
  };

  // `load` reads latest state via closure; re-run only when filter changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const logout = async () => { await api.post("/auth/logout"); nav("/admin/login"); };

  const changeStatus = async (contact_id, status) => {
    await api.patch(`/admin/contacts/${contact_id}`, { status });
    load();
  };

  const approveApp = async (id) => {
    const r = await api.post(`/admin/diagnostic-applications/${id}/approve`);
    alert(`Approved. Private checkout URL:\n\n${r.data.checkout_url}\n\n(Also emailed to applicant.)`);
    load();
  };

  const rejectApp = async (id) => {
    if (!window.confirm("Reject and mark Not a Fit?")) return;
    await api.post(`/admin/diagnostic-applications/${id}/reject`);
    load();
  };

  const saveSession = async () => {
    setSessionSaving(true); setSessionSaved(false);
    try {
      const payload = {
        next_datetime: session.next_datetime,
        timezone_label: session.timezone_label,
        seats_remaining: Number(session.seats_remaining),
        custom_note: session.custom_note,
        is_active: !!session.is_active,
      };
      const r = await api.put("/admin/growth-track/session", payload);
      setSession(prev => ({ ...prev, ...r.data }));
      setSessionSaved(true);
      setTimeout(() => setSessionSaved(false), 2500);
    } finally { setSessionSaving(false); }
  };

  const sendFollowUp = async (contact_id, template) => {
    if (!template) return;
    try {
      await api.post(`/admin/contacts/${contact_id}/send-followup`, { template });
      alert(`Follow-up "${template}" sent.`);
      load();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const runTriage = async (application_id) => {
    setTriageBusy(prev => ({ ...prev, [application_id]: true }));
    try {
      const r = await api.post(`/admin/strategy-call-applications/${application_id}/triage`);
      setClarityApps(prev => prev.map(a =>
        a.application_id === application_id ? { ...a, ai_triage: r.data.triage } : a
      ));
    } catch (e) {
      alert(`Triage failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTriageBusy(prev => ({ ...prev, [application_id]: false }));
    }
  };

  if (loading && !me) return <div className="min-h-screen bg-[#0A0A0A] text-[#A1A1AA] flex items-center justify-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E6E6E6]">
      <SEO title="Admin | Driver Reborn Advisory" description="" noindex={true} />
      <header className="border-b border-[#2B2B2B]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-bebas text-2xl tracking-wider">DRIVER REBORN — ADMIN</div>
            {me && <div className="text-xs text-[#A1A1AA]">{me.email}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-ghost" data-testid="admin-refresh"><RefreshCw size={16}/> Refresh</button>
            <button onClick={logout} className="btn-ghost" data-testid="admin-logout"><LogOut size={16}/> Logout</button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex gap-6 text-sm">
          {["overview", "contacts", "applications", "clarity-calls", "settings"].map(t => (
            <button key={t} onClick={()=>setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`py-3 uppercase tracking-widest border-b-2 ${tab === t ? "border-[#FF6A00] text-[#FF6A00]" : "border-transparent"}`}>
              {t}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "overview" && summary && (
          <div>
            <EmailDeliveryPanel />
            <FunnelStrip />
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <Stat label="Total Contacts" value={summary.total_contacts} />
              <Stat label="Quiz Completions" value={summary.quiz_completions} />
              <Stat label="Diagnostic Applications" value={summary.diagnostic_applications} />
              <Stat label="Paid Transactions" value={summary.paid_transactions} />
            </div>
            <h3 className="font-bebas text-2xl uppercase mb-3">By Status</h3>
            <div className="grid md:grid-cols-3 gap-3 mb-8">
              {Object.entries(summary.by_status).map(([k, v]) => (
                <div key={k} className="border border-[#2B2B2B] bg-[#111111] p-4 flex justify-between">
                  <span className="text-sm text-[#A1A1AA]">{k}</span>
                  <span className="font-bebas text-xl text-[#FF6A00]">{v}</span>
                </div>
              ))}
            </div>
            <h3 className="font-bebas text-2xl uppercase mb-3">Recent Events</h3>
            <div className="border border-[#2B2B2B] bg-[#111111]">
              {summary.recent_events.slice(0, 20).map((e) => (
                <div key={`${e.at}-${e.event}-${e.path}`} className="px-4 py-2 border-b border-[#2B2B2B] text-xs flex justify-between">
                  <span>{e.event} · {e.path}</span>
                  <span className="text-[#A1A1AA]">{new Date(e.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div>
            <div className="flex flex-wrap gap-3 mb-4 items-center">
              <div className="flex items-center gap-2 bg-[#111111] border border-[#2B2B2B] px-3">
                <Search size={16} className="text-[#A1A1AA]"/>
                <input placeholder="Search email/name/company" value={search} onChange={e=>setSearch(e.target.value)}
                  onKeyDown={e=>e.key==="Enter" && load()}
                  data-testid="admin-search"
                  className="bg-transparent py-2 outline-none text-sm w-64"/>
              </div>
              <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
                data-testid="admin-status-filter"
                className="bg-[#111111] border border-[#2B2B2B] px-3 py-2 text-sm">
                <option value="">All statuses</option>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
              <a href={`${API_BASE}/admin/contacts/export`} className="btn-ghost" data-testid="admin-export">
                <Download size={16}/> Export CSV
              </a>
              <span className="text-sm text-[#A1A1AA] ml-auto">{contacts.length} results</span>
            </div>
            <div className="border border-[#2B2B2B] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#111111]">
                  <tr className="text-left uppercase text-xs tracking-widest text-[#A1A1AA]">
                    <th className="p-3">Email</th>
                    <th className="p-3">Name</th>
                    <th className="p-3">Company</th>
                    <th className="p-3">Source</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Follow-up</th>
                    <th className="p-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(c => (
                    <tr key={c.contact_id} className="border-t border-[#2B2B2B]" data-testid={`contact-row-${c.contact_id}`}>
                      <td className="p-3">{c.email}</td>
                      <td className="p-3">{c.name || "—"}</td>
                      <td className="p-3">{c.company || "—"}</td>
                      <td className="p-3 text-[#A1A1AA]">{c.source}</td>
                      <td className="p-3">
                        <select value={c.status} onChange={e=>changeStatus(c.contact_id, e.target.value)}
                          data-testid={`status-select-${c.contact_id}`}
                          className="bg-[#0A0A0A] border border-[#2B2B2B] px-2 py-1">
                          {STATUSES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="p-3">
                        <select defaultValue="" onChange={e=>{ sendFollowUp(c.contact_id, e.target.value); e.target.value=""; }}
                          data-testid={`followup-select-${c.contact_id}`}
                          className="bg-[#0A0A0A] border border-[#2B2B2B] px-2 py-1">
                          <option value="">Send email…</option>
                          <option value="growth_track_fit">Growth Track fit</option>
                          <option value="diagnostic_fit">Diagnostic fit</option>
                          <option value="not_ready">Not ready yet</option>
                          <option value="did_not_book">Did not book</option>
                        </select>
                      </td>
                      <td className="p-3 text-[#A1A1AA] text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "applications" && (
          <div className="space-y-4">
            {applications.length === 0 && <p className="text-[#A1A1AA]">No applications yet.</p>}
            {applications.map(a => (
              <div key={a.application_id} className="border border-[#2B2B2B] bg-[#111111] p-5" data-testid={`app-card-${a.application_id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-bebas text-2xl">{a.data.name} — {a.data.company}</div>
                    <div className="text-xs text-[#A1A1AA]">{a.email} · {a.data.phone}</div>
                  </div>
                  <span className={`text-xs uppercase px-2 py-1 border ${a.status === "approved" ? "text-[#FF6A00] border-[#FF6A00]" : "text-[#A1A1AA] border-[#2B2B2B]"}`}>
                    {a.status}
                  </span>
                </div>
                <div className="grid md:grid-cols-3 gap-3 text-xs mb-3">
                  <div><span className="text-[#A1A1AA]">Fleet:</span> {a.data.fleet_size}</div>
                  <div><span className="text-[#A1A1AA]">Revenue:</span> {a.data.annual_revenue}</div>
                  <div><span className="text-[#A1A1AA]">Ready to invest:</span> {a.data.ready_to_invest ? "Yes" : "No"}</div>
                </div>
                <p className="text-sm mb-3">{a.data.biggest_challenge}</p>
                {a.status === "pending_review" && (
                  <div className="flex gap-2">
                    {paymentLinked ? (
                      <button onClick={()=>approveApp(a.application_id)} className="btn-primary" data-testid={`approve-${a.application_id}`}>Approve & Send Checkout</button>
                    ) : (
                      <button disabled className="btn-ghost opacity-60 cursor-not-allowed" title="Connect a real payment link to enable" data-testid={`approve-disabled-${a.application_id}`}>Approval disabled — no payment link</button>
                    )}
                    <button onClick={()=>rejectApp(a.application_id)} className="btn-ghost" data-testid={`reject-${a.application_id}`}>Reject</button>
                  </div>
                )}
                {a.status === "approved" && a.checkout_token && (
                  <div className="text-xs text-[#A1A1AA] break-all">
                    Private link: {window.location.origin}/diagnostic/checkout?token={a.checkout_token}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "clarity-calls" && (
          <div className="space-y-4">
            <p className="text-sm text-[#A1A1AA] mb-2">
              Fleet Owner Conversation requests (18 questions). AI Triage is a recommendation — you approve, decline, or route.
            </p>
            {clarityApps.length === 0 && <p className="text-[#A1A1AA]">No Fleet Owner Conversation requests yet.</p>}
            {clarityApps.map(a => {
              const d = a.data || {};
              const t = a.ai_triage;
              return (
                <div key={a.application_id} className="border border-[#2B2B2B] bg-[#111111] p-5" data-testid={`clarity-card-${a.application_id}`}>
                  <div className="flex justify-between items-start mb-3 gap-3 flex-wrap">
                    <div>
                      <div className="font-bebas text-2xl">{d.first_name} — {d.company}</div>
                      <div className="text-xs text-[#A1A1AA]">{a.email} · {d.phone || "no phone"}</div>
                      <div className="text-xs text-[#A1A1AA] mt-1">
                        Trucks: {d.active_trucks} · Revenue: {d.annual_revenue} · Deterministic lane: <span className="text-[#FF6A00]">{a.lane}</span>
                      </div>
                    </div>
                    <button onClick={() => runTriage(a.application_id)}
                      disabled={!!triageBusy[a.application_id]}
                      className="btn-primary" data-testid={`clarity-triage-${a.application_id}`}>
                      {triageButtonLabel(triageBusy[a.application_id], t)}
                    </button>
                  </div>
                  {d.primary_problem && (
                    <div className="text-sm text-[#E6E6E6] leading-relaxed mb-3">
                      <span className="text-[#A1A1AA] uppercase tracking-widest text-xs">Primary problem:</span> {d.primary_problem}
                    </div>
                  )}
                  {t && (
                    <div className="border-t border-[#2B2B2B] mt-3 pt-4" data-testid={`clarity-triage-result-${a.application_id}`}>
                      <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">
                        AI recommendation: <span className="text-[#E6E6E6]">{t.recommended_lane}</span>
                        <span className="text-[#A1A1AA] normal-case ml-2">(Brandon has final say — not automatic)</span>
                      </div>
                      <p className="text-sm text-[#E6E6E6] mb-3">{t.fit_summary}</p>
                      {Array.isArray(t.fit_bullets) && t.fit_bullets.length > 0 && (
                        <ul className="space-y-1 mb-3">
                          {t.fit_bullets.map((b, i) => (
                            <li key={`${a.application_id}-fb-${i}`} className="text-sm text-[#E6E6E6] flex items-start gap-2">
                              <span className="text-[#FF6A00] font-bebas mt-0.5 flex-shrink-0">■</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {t.rationale && (
                        <p className="text-sm text-[#A1A1AA] italic mb-3">Rationale: {t.rationale}</p>
                      )}
                      {Array.isArray(t.risk_flags) && t.risk_flags.length > 0 && (
                        <div className="mb-2">
                          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-1">Risk flags</div>
                          <ul className="space-y-1">
                            {t.risk_flags.map((f, i) => (
                              <li key={`${a.application_id}-rf-${i}`} className="text-xs text-[#E6E6E6]">— {f}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {t.note_to_brandon && (
                        <div className="text-xs text-[#A1A1AA] mt-2">
                          <b>Reply draft:</b> {t.note_to_brandon}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === "settings" && (
          <div className="max-w-2xl">
            <h2 className="font-bebas text-3xl uppercase mb-2">Growth Track — Next Live Call</h2>
            <p className="text-sm text-[#A1A1AA] mb-6">
              This renders as a bright orange strip above the checkout box on <code className="text-[#FF6A00]">/growth-track</code>. Turn it off with the toggle when the roster is closed.
            </p>
            <div className="border border-[#2B2B2B] bg-[#111111] p-6 space-y-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!session.is_active}
                  onChange={e=>setSession({...session, is_active: e.target.checked})}
                  data-testid="session-active"
                  className="accent-[#FF6A00] w-4 h-4"/>
                <span className="text-sm uppercase tracking-widest">Show strip on Growth Track page</span>
              </label>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Next call date / time (free text)</label>
                <input type="text" value={session.next_datetime || ""} placeholder="e.g. Wednesday 7:00 PM"
                  onChange={e=>setSession({...session, next_datetime: e.target.value})}
                  data-testid="session-datetime"
                  className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Timezone label</label>
                  <input type="text" value={session.timezone_label || ""} placeholder="CT"
                    onChange={e=>setSession({...session, timezone_label: e.target.value})}
                    data-testid="session-tz"
                    className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Seats remaining</label>
                  <input type="number" min="0" value={session.seats_remaining ?? ""}
                    onChange={e=>setSession({...session, seats_remaining: e.target.value})}
                    data-testid="session-seats"
                    className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Custom note (optional)</label>
                <input type="text" value={session.custom_note || ""} placeholder="e.g. Roster closes 24hrs before call"
                  onChange={e=>setSession({...session, custom_note: e.target.value})}
                  data-testid="session-note"
                  className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
              </div>
              <div className="flex items-center gap-4">
                <button onClick={saveSession} disabled={sessionSaving} className="btn-primary" data-testid="session-save">
                  {sessionSaving ? "Saving…" : "Save Session"}
                </button>
                {sessionSaved && <span className="text-[#22C55E] text-sm" data-testid="session-saved">Saved.</span>}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#2B2B2B] bg-[#111111] p-4">
      <div className="text-xs uppercase tracking-widest text-[#A1A1AA]">{label}</div>
      <div className="font-bebas text-4xl text-[#FF6A00] mt-1">{value}</div>
    </div>
  );
}
